import React, { Suspense, FC, ReactElement, useMemo, MouseEvent, useEffect } from 'react';
import classnames from 'classnames';
import { useRouter, useSignal, ErrorBoundary } from '@funtech-int/rival-react';
import {
    Tooltip,
    useFeatureFlags,
    usePaymentPlace,
    type PaymentPlace,
    useAuthManager,
} from '@funtech-int/yango-shared';
import { AvailabilityAnnounce, NextEpisode } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { createSignal } from '@funtech-int/rival';
import { useStorage } from '@funtech-int/web-toolkit';
import { TarifficatorPurchaseSuccessWidgetEvent } from '@funtech-int/yango-shared';

import { checkIsTvSeries } from '@/shared/lib/movie';
import { Button } from '@/shared/components/Button/Button';
import { PlusPaymentPlace } from '@/shared/constants/payments';
import { useHasSubscription } from '@/shared/hooks/useHasSubscription';
import { MediaBillingFeature } from '@/shared/constants/billingFeatures';
import { useMovieUGC } from '@/shared/hooks/useMovieUGC';
import { useLoginUrl } from '@/shared/hooks/useLoginUrl';
import PlayIcon from '@/shared/assets/MoviePlay';
import { UnbookmarkIcon } from '@/shared/assets/Unbookmark';
import { BookmarkIcon } from '@/shared/assets/Bookmark';
import ForbiddenOn from '@/shared/assets/ForbiddenOn';
import Forbidden from '@/shared/assets/Forbidden';
import { startPlaying } from '@/scenarios/legacy-player/components/player-manager/helpers/startPlaying';
import { PlayerContentType } from '@/scenarios/legacy-player/components/feature-player/types/content';
import { setPlayingParams } from '@/scenarios/legacy-player/components/feature-player/utils/set-playing-params';
import { usePaymentPage } from '@/shared/hooks/usePaymentPage';
import { getAutoOpenWidgetUrl } from '@/shared/lib/getAutoOpenWidgetUrl';
import { useOfferWebAnalytics } from '@/shared/hooks/useOfferWebAnalytics';
import { OfferAnalytics } from '@/shared/lib/analytics/offer';
import { useSubscriptionRefetch } from '@/shared/hooks/useSubscriptionRefetch';
import { usePaymentWidgetModal } from '@/shared/hooks/usePopups';
import { enrichPromoParamsIfNeed } from '@/scenarios/legacy-player/components/player-manager/helpers/enrich-promo-params-if-need';

import { featureFlagsScheme } from '../../lib/featureFlags';
import { contentActionsBuilder, ContentActionsConfig } from '../../lib/contentActionsBuilder';
import { useI18n } from '../../hooks/useI18n';
import { filmRoute } from '../../routes';
import { AnnounceContent } from '../AnnounceContent/AnnounceContent';
import { OfferContent } from '../OfferContent/OfferContent';
import { useMoviePromoContentActions } from '../../hooks/useMoviePromoContentActions';

import styles from './MoviePromoContentActions.module.css';

interface MoviePromoContentActionsContentAnalytics {
    itemImpressionNavigated(this: void, to: 'payment' | 'player' | 'movie_card'): void;
    movieImpressionMarked(
        this: void,
        type: 'bookmarked' | 'unbookmarked' | 'markedAsNotInteresting' | 'unmarkedAsNotInteresting'
    ): void;
}

export type MoviePromoContentActionsAnalytics = MoviePromoContentActionsContentAnalytics &
    OfferAnalytics;

interface MoviePromoContentActionsComponentProps {
    contentActionsConfig: ContentActionsConfig;
    contentId: string;
    movieId: number;
    isTvSeries: boolean;
    announce: AvailabilityAnnounce | null;
    billingFeatureName: string;
    nextEpisode?: NextEpisode;
    onMount?: () => void;
    onDislike?: () => void;
    analytics?: MoviePromoContentActionsAnalytics;
    paymentPlace?: PaymentPlace;
}

const LEAN_BILLING_FEATURES: string[] = [
    MediaBillingFeature.CRUNCHYROLL,
    MediaBillingFeature.AMEDIATEKA,
];

const storageHintKey = 'promoblock_negative_feedback_hint_hidden';

const MoviePromoContentActionsComponent: FC<MoviePromoContentActionsComponentProps> = ({
    contentActionsConfig,
    contentId,
    movieId,
    isTvSeries,
    announce,
    nextEpisode,
    billingFeatureName,
    onDislike,
    onMount,
    analytics,
    paymentPlace,
}) => {
    const i18n = useI18n();
    const router = useRouter();
    const { flags } = useFeatureFlags(featureFlagsScheme);
    const isNewPlayerEnabled = flags.get('isNewPlayerEnabled');
    const hasSubscription = useHasSubscription();
    const { isAuthorized } = useAuthManager();
    const loginUrl = useLoginUrl();
    const movieUGC = useMovieUGC(movieId);
    const storage = useStorage();
    const { refetch: subscriptionRefetch } = useSubscriptionRefetch();

    const offer = paymentPlace?.offers?.[0];
    const { purchaseSucceeded: sendSuccessPurchaseEvent } = useOfferWebAnalytics(offer);
    const offerLoginUrl = useLoginUrl(getAutoOpenWidgetUrl(offer));

    const paymentWidget = usePaymentWidgetModal();

    const controller = useMemo(() => {
        const hintVisible = createSignal(storage.local.get(storageHintKey) !== 1);

        function playContent() {
            if (isTvSeries && nextEpisode?.contentId) {
                startPlaying({
                    router,
                    seriesInfo: {
                        seasonNumber: nextEpisode.seasonNumber ?? 1,
                        episodeNumber: nextEpisode.episodeNumber ?? 1,
                    },
                    contentId: nextEpisode.contentId,
                    contentType: PlayerContentType.EPISODE,
                    additionalQueryParams: enrichPromoParamsIfNeed({}),
                    isNewPlayerEnabled,
                });
                return;
            }

            startPlaying({
                router,
                contentId,
                contentType: PlayerContentType.FILM,
                isNewPlayerEnabled,
            });
        }

        function onSuccessPurchase(data: TarifficatorPurchaseSuccessWidgetEvent) {
            subscriptionRefetch();
            sendSuccessPurchaseEvent(data);
            if (offer) {
                analytics?.succeeded(offer);
            }

            router.navigate({
                to: filmRoute({ id: contentId }),
            });
        }

        return {
            hintVisible,

            onOfferShow: () => {
                if (offer) {
                    paymentPlace?.analytics.offerShowed(offer);
                    analytics?.showed(offer);
                }
            },
            onOfferClick: () => {
                analytics?.itemImpressionNavigated('payment');
                if (!offer) return;

                paymentPlace?.analytics.offerClicked(offer);
                analytics?.navigated(offer);

                paymentWidget.open(paymentPlace, offer, {
                    onPurchaseSuccess: onSuccessPurchase,
                    onSuccessClose: playContent,
                    onNeedAuth: () => window.location.assign(offerLoginUrl),
                });
            },

            onWatchClick: () => {
                analytics?.itemImpressionNavigated('player');

                setPlayingParams({
                    promoContentId: contentId,
                });

                playContent();
            },

            onAboutMovieClick: (e: MouseEvent<HTMLAnchorElement>) => {
                e.preventDefault();
                analytics?.itemImpressionNavigated('movie_card');
                router.navigate({ to: filmRoute({ id: contentId }) });
            },

            onPlannedToWatchClick: () => {
                analytics?.movieImpressionMarked(
                    movieUGC.plannedToWatch.get() ? 'unbookmarked' : 'bookmarked'
                );

                if (!isAuthorized) {
                    window.location.assign(loginUrl);
                    return;
                }

                movieUGC.togglePlannedToWatch();
            },

            onNotInterestedClick: async () => {
                analytics?.movieImpressionMarked(
                    movieUGC.notInterested.get()
                        ? 'unmarkedAsNotInteresting'
                        : 'markedAsNotInteresting'
                );

                if (!isAuthorized) {
                    window.location.assign(loginUrl);
                    return;
                }

                storage.local.set(storageHintKey, 1);
                hintVisible.set(false);

                await movieUGC.toggleNotInterested();
                onDislike?.();
            },
        };
    }, [
        analytics,
        contentId,
        isAuthorized,
        isNewPlayerEnabled,
        isTvSeries,
        loginUrl,
        movieUGC,
        nextEpisode,
        offer,
        onDislike,
        paymentWidget,
        paymentPlace,
        router,
        storage.local,
        subscriptionRefetch,
        sendSuccessPurchaseEvent,
        offerLoginUrl,
    ]);

    const plannedToWatch = useSignal(movieUGC.plannedToWatch);
    const notInterested = useSignal(movieUGC.notInterested);
    const hintVisible = useSignal(controller.hintVisible);

    useEffect(() => {
        onMount?.();
    }, [onMount]);

    let content: ReactElement | undefined;
    let note: ReactElement | undefined;

    if (contentActionsConfig.content?.type === 'offer' && offer) {
        content = <OfferContent billingFeatureName={billingFeatureName} offer={offer} />;
    } else if (contentActionsConfig.content?.type === 'announce') {
        content = (
            <AnnounceContent
                announcePromise={announce?.announcePromise ?? null}
                availabilityDate={announce?.availabilityDate ?? null}
            />
        );
    }

    if (
        contentActionsConfig.note?.type === 'subscription' &&
        offer &&
        !hasSubscription &&
        LEAN_BILLING_FEATURES.some((billingFeature) => billingFeature === billingFeatureName)
    ) {
        note = <div className={styles.subscriptionNote}>{offer.offerTexts.subscriptionName}</div>;
    }

    return (
        <div>
            {content}
            <div className={styles.actions}>
                {contentActionsConfig.actions.map((action) => {
                    if (action.type === 'watch') {
                        return (
                            <Button
                                key={action.type}
                                name="Watch"
                                variant="accent"
                                size={60}
                                startIcon={<PlayIcon fill="#fff" />}
                                className={classnames(styles.button, styles.watch)}
                                data-test-id="MainButton_watch"
                                onClick={controller.onWatchClick}
                            >
                                {i18n('content_actions__watch_text')}
                            </Button>
                        );
                    }

                    if (action.type === 'offer') {
                        if (!offer) {
                            return null;
                        }

                        return (
                            <Button
                                key={action.type}
                                size={60}
                                variant={!isAuthorized ? 'accent' : 'plus'}
                                startIcon={<PlayIcon fill="#fff" />}
                                onClick={controller.onOfferClick}
                                className={classnames(styles.button, styles.offer)}
                                name="Offer"
                                data-test-id="MovieOfferButton"
                                onShow={controller.onOfferShow}
                            >
                                {i18n('content_actions__watch_text')}
                            </Button>
                        );
                    }

                    if (action.type === 'aboutMovie') {
                        const aboutButtonText = isTvSeries
                            ? i18n('about_button__series_text')
                            : i18n('about_button__movie_text');

                        return (
                            <Button
                                as="a"
                                key={action.type}
                                href={router.serialize(filmRoute({ id: contentId }))}
                                variant="primary"
                                size={60}
                                theme="dark"
                                className={styles.button}
                                aria-label={aboutButtonText}
                                data-test-id="ContentActions_aboutButton"
                                onClick={controller.onAboutMovieClick}
                            >
                                {aboutButtonText}
                            </Button>
                        );
                    }

                    if (action.type === 'plannedToWatch') {
                        const plannedToWatchText = i18n('planned_to_watch__default_text');

                        return (
                            <Button
                                key={action.type}
                                name="Bookmark"
                                variant={announce ? 'accentWhite' : 'primary'}
                                size={60}
                                theme="dark"
                                startIcon={
                                    plannedToWatch ? (
                                        <UnbookmarkIcon fill="var(--primary-color)" />
                                    ) : (
                                        <BookmarkIcon fill={announce ? '#000' : '#FFF'} />
                                    )
                                }
                                className={styles.button}
                                aria-label={announce ? plannedToWatchText : undefined}
                                aria-pressed={plannedToWatch}
                                data-test-id="ContentActions_bookmarkButton"
                                onClick={controller.onPlannedToWatchClick}
                            >
                                {announce ? plannedToWatchText : null}
                            </Button>
                        );
                    }

                    if (action.type === 'notInteresting') {
                        const notInterestedText = i18n('content_actions__not_interested_text');

                        return (
                            <div
                                className={classnames(styles.button, styles.withHint)}
                                key={action.type}
                            >
                                <Tooltip
                                    direction="top"
                                    alignment="middle"
                                    visible={hintVisible}
                                    showCloseButton={false}
                                    className={styles.hint}
                                    withOuterClick={false}
                                    data-test-id="ContentActions_hint"
                                >
                                    {i18n('content_actions__not_interested_tooltip')}
                                </Tooltip>
                                <Button
                                    name="Not interested"
                                    variant="primary"
                                    size={60}
                                    theme="dark"
                                    startIcon={
                                        notInterested ? (
                                            <ForbiddenOn fill="var(--primary-color)" />
                                        ) : (
                                            <Forbidden fill="#FFF" />
                                        )
                                    }
                                    aria-label={notInterestedText}
                                    aria-pressed={notInterested}
                                    data-test-id="ContentActions_notInterestedButton"
                                    onClick={controller.onNotInterestedClick}
                                />
                            </div>
                        );
                    }

                    return null;
                })}
            </div>
            {note}
        </div>
    );
};

const MoviePromoContentActionsOffer: FC<MoviePromoContentActionsComponentProps> = (props) => {
    const page = usePaymentPage();
    const { billingFeatureName } = props;

    const paymentPlace = usePaymentPlace({
        places: [PlusPaymentPlace.WatchButton],
        page,
        extraParams: {
            customProperties: { billingFeatureName },
        },
    });

    return <MoviePromoContentActionsComponent {...props} paymentPlace={paymentPlace} />;
};

interface MoviePromoContentActionsProps {
    contentId: string;
    onDislike?: () => void;
    onMount?: () => void;
    analytics?: MoviePromoContentActionsAnalytics;
}

const MoviePromoContentActionsContainer: FC<MoviePromoContentActionsProps> = ({
    contentId,
    onDislike,
    onMount,
    analytics,
}) => {
    const { contentActions, watchingProgress } = useMoviePromoContentActions({ contentId });

    const contentActionsConfig = useMemo(() => {
        const builder = contentActionsBuilder()
            .withViewOption(contentActions?.viewOption)
            .withAnnounce(contentActions?.announce)
            .withAboutMovie()
            .withNotInteresting();

        return builder.build();
    }, [contentActions]);

    if (!contentActions) {
        return null;
    }

    const ContentActionsComponent =
        contentActions.viewOption?.isPurchasable &&
        contentActions.viewOption?.isWatchable &&
        !contentActions.announce
            ? MoviePromoContentActionsOffer
            : MoviePromoContentActionsComponent;

    return (
        <ContentActionsComponent
            contentActionsConfig={contentActionsConfig}
            contentId={contentId}
            movieId={contentActions.id}
            isTvSeries={checkIsTvSeries(contentActions.typename)}
            announce={contentActions.announce}
            nextEpisode={watchingProgress?.nextEpisode ?? undefined}
            billingFeatureName={
                (contentActions.viewOption?.purchaseOptionCustomProperties
                    ?.billingFeatureName as string) ?? ''
            }
            onDislike={onDislike}
            onMount={onMount}
            analytics={analytics}
        />
    );
};

export const MoviePromoContentActions: FC<MoviePromoContentActionsProps> = ({
    contentId,
    onDislike,
    onMount,
    analytics,
}) => (
    <ErrorBoundary fallback={() => null}>
        <Suspense fallback={null}>
            <MoviePromoContentActionsContainer
                contentId={contentId}
                onDislike={onDislike}
                onMount={onMount}
                analytics={analytics}
            />
        </Suspense>
    </ErrorBoundary>
);
